import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon, LoaderIcon, LockIcon } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useCheckoutForm, type CheckoutFields, type PaymentMethod } from '../hooks/useCheckoutForm';
import { shippingCost, shippingOptions, FREE_SHIPPING_THRESHOLD } from '../utils/shipping';
import { formatPrice } from '../utils/currency';
import { OrderSummary } from '../components/checkout/OrderSummary';
const payments: {
  id: PaymentMethod;
  label: string;
}[] = [{
  id: 'ideal',
  label: 'iDEAL'
}, {
  id: 'card',
  label: 'Creditcard'
}, {
  id: 'klarna',
  label: 'Klarna — achteraf betalen'
}, {
  id: 'paypal',
  label: 'PayPal'
}];
export function Checkout() {
  const {
    lines,
    subtotal,
    clear
  } = useCart();
  const form = useCheckoutForm();
  const [status, setStatus] = useState<'idle' | 'processing' | 'done'>('idle');
  const [order, setOrder] = useState<{
    number: string;
    total: number;
  } | null>(null);
  const shipping = shippingCost(subtotal, form.shipping);
  const total = subtotal + shipping;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.validate()) return;
    setStatus('processing');
    window.setTimeout(() => {
      setOrder({
        number: `AM-${Math.floor(100000 + Math.random() * 900000)}`,
        total
      });
      clear();
      setStatus('done');
    }, 900);
  };
  return <div className="min-h-screen w-full bg-canvas" data-id="element-397" data-mp="3gf6qg7po1ku7">
      <header className="border-b border-line" data-id="element-398" data-mp="3ld8buzakdw8u">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8" data-id="element-399" data-mp="3le1999nmv9eb">
          <Link to="/" className="text-xl font-semibold tracking-[0.24em]" data-id="element-400" data-mp="3qbjkx7t1c2q0">AMIRA</Link>
          <span className="flex items-center gap-2 text-sm text-muted" data-id="element-401" data-mp="3ue6f3fd6hzqd">
            <LockIcon className="h-4 w-4" data-id="element-402" data-mp="15mdpkfxmrddg" /> Veilig afrekenen
          </span>
        </div>
      </header>

      {status === 'done' && order ? <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className="mx-auto flex max-w-lg flex-col items-center px-5 py-24 text-center" data-id="element-403" data-mp="19me7iajxu6p9">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-olive text-canvas" data-id="element-404" data-mp="3fp9pxhwegjum">
            <CheckIcon className="h-6 w-6" data-id="element-405" data-mp="3apewb6l8pg5v" />
          </span>
          <h1 className="mt-6 text-4xl font-semibold tracking-display" data-id="element-406" data-mp="3ao3v9ej6imig">Bedankt, {form.fields.firstName}!</h1>
          <p className="mt-3 text-lg text-muted" data-id="element-407" data-mp="2iyqzu7uigyyj">
            Bestelling <strong className="text-ink" data-id="element-408" data-mp="2j0bvy1wh4pyi">{order.number}</strong> ({formatPrice(order.total)}) is ontvangen. Je ontvangt de
            bevestiging op {form.fields.email}.
          </p>
          <p className="mt-6 rounded-2xl bg-surface px-5 py-4 text-sm" data-id="element-409" data-mp="2rydzao3ojuoy">Verwachte levering: <strong data-id="element-410" data-mp="249crn32o8nkq">morgen</strong></p>
          <Link to="/" className="mt-8 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-clay" data-id="element-411" data-mp="2489zkrtm4f38">
            Verder winkelen
          </Link>
        </motion.div> : lines.length === 0 ? <div className="mx-auto flex max-w-lg flex-col items-center px-5 py-32 text-center" data-id="element-412" data-mp="jjfqig6h3m0h">
          <h1 className="text-3xl font-semibold tracking-display" data-id="element-413" data-mp="5s1562p3f8q9">Je winkelwagen is leeg</h1>
          <p className="mt-2 text-muted" data-id="element-414" data-mp="5q7vsfn3iaek">Voeg eerst iets toe om af te rekenen.</p>
          <Link to="/collectie/bestsellers" className="mt-8 inline-flex h-12 items-center rounded-full bg-ink px-6 text-sm font-medium text-canvas hover:bg-clay" data-id="element-415" data-mp="ap1agbe4c7gb">
            Shop bestsellers
          </Link>
        </div> : <form onSubmit={submit} noValidate className="mx-auto grid max-w-6xl gap-10 px-5 py-10 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-14" data-id="element-416" data-mp="oisjlinxtpbi">
          <div className="space-y-10 lg:col-span-7" data-id="element-417" data-mp="tgbwof8wfw1v">
            <h1 className="text-3xl font-semibold tracking-display sm:text-4xl" data-id="element-418" data-mp="39916mkohol51">Afrekenen</h1>

            <fieldset data-id="element-419" data-mp="3e7uhs6gb0021">
              <legend className="text-lg font-semibold" data-id="element-420" data-mp="3e84c268dstdj">Contact</legend>
              <div className="mt-4" data-id="element-421" data-mp="3thhhy49gd2tb">
                <Field id="email" label="E-mailadres" type="email" autoComplete="email" form={form} data-id="element-422" data-mp="3tij3j2o3h7pa" />
              </div>
            </fieldset>

            <fieldset data-id="element-423" data-mp="3ooy3z66us5nz">
              <legend className="text-lg font-semibold" data-id="element-424" data-mp="3koxpjljr7ugx">Bezorgadres</legend>
              <div className="mt-4 grid gap-4 sm:grid-cols-2" data-id="element-425" data-mp="3kmtbwbw1zh3g">
                <Field id="firstName" label="Voornaam" autoComplete="given-name" form={form} data-id="element-426" data-mp="3frdyfl1yjn7k" />
                <Field id="lastName" label="Achternaam" autoComplete="family-name" form={form} data-id="element-427" data-mp="g9o7cdlqfsnl" />
                <div className="sm:col-span-2" data-id="element-428" data-mp="cap4cyjka5ys">
                  <Field id="address" label="Straat en huisnummer" autoComplete="street-address" form={form} data-id="element-429" data-mp="7aaudayegly4" />
                </div>
                <Field id="postcode" label="Postcode" autoComplete="postal-code" form={form} data-id="element-430" data-mp="2bzjcwwdc5eb" />
                <Field id="city" label="Plaats" autoComplete="address-level2" form={form} data-id="element-431" data-mp="2s0s56zwm47hm" />
              </div>
            </fieldset>

            <fieldset data-id="element-432" data-mp="2mzue1xtmk33z">
              <legend className="text-lg font-semibold" data-id="element-433" data-mp="2n3ryqnvup2pz">Verzending</legend>
              <div className="mt-4 grid gap-2" data-id="element-434" data-mp="2cz1ta818ngh0">
                {shippingOptions.map(o => {
              const price = shippingCost(subtotal, o.id);
              return <Option key={o.id} name="shipping" checked={form.shipping === o.id} onChange={() => form.setShipping(o.id)} data-id="element-435" data-mp="2muwgzhkbk1qn">
                      <span className="flex-1" data-id="element-436" data-mp="2rohi3ih7lbu2">
                        <span className="block font-medium" data-id="element-437" data-mp="2vorjsixyxg34">{o.label}</span>
                        <span className="block text-sm text-muted" data-id="element-438" data-mp="30mt394zb3hf4">{o.detail}</span>
                      </span>
                      <span className="text-sm font-medium" data-id="element-439" data-mp="35lml19lmlul3">{price === 0 ? 'Gratis' : formatPrice(price)}</span>
                    </Option>;
            })}
                {subtotal < FREE_SHIPPING_THRESHOLD && <p className="text-sm text-muted" data-id="element-440" data-mp="1b9pncywlbshl">Nog {formatPrice(FREE_SHIPPING_THRESHOLD - subtotal)} tot gratis standaard verzending.</p>}
              </div>
            </fieldset>

            <fieldset data-id="element-441" data-mp="1871xy7j28od0">
              <legend className="text-lg font-semibold" data-id="element-442" data-mp="188mkpaz4v30b">Betaalmethode</legend>
              <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-443" data-mp="13905ivzqvcov">
                {payments.map(p => <Option key={p.id} name="payment" checked={form.payment === p.id} onChange={() => form.setPayment(p.id)} data-id="element-444" data-mp="1i60xn0lul9nv">
                    <span className="font-medium" data-id="element-445" data-mp="1d7zc890y8y98">{p.label}</span>
                  </Option>)}
              </div>
            </fieldset>

            <div className="lg:hidden" data-id="element-446" data-mp="1m5jhru5uhgpn">
              <OrderSummary lines={lines} subtotal={subtotal} shipping={shipping} data-id="element-447" data-mp="1m4gr9ncfpga9" />
            </div>

            <button type="submit" disabled={status === 'processing'} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-ink text-base font-medium text-canvas transition-colors duration-150 hover:bg-clay disabled:opacity-70" data-id="element-448" data-mp="20x1u9hzhwt49">
              {status === 'processing' ? <>
                  <LoaderIcon className="h-4 w-4 animate-spin" data-id="element-449" data-mp="2ojdue3enk82n" /> Bezig met betalen…
                </> : <>Betaal {formatPrice(total)}</>}
            </button>
            <p className="-mt-6 text-center text-xs text-muted" data-id="element-450" data-mp="2fhmn4u4grt6o">30 dagen bedenktijd · Veilig betalen via SSL</p>
          </div>

          <div className="hidden lg:col-span-5 lg:block" data-id="element-451" data-mp="25s33vncu9xba">
            <div className="sticky top-8" data-id="element-452" data-mp="25qjmtujczi8g">
              <OrderSummary lines={lines} subtotal={subtotal} shipping={shipping} data-id="element-453" data-mp="2aijl7ijs632x" />
            </div>
          </div>
        </form>}
    </div>;
}
type FieldProps = {
  id: keyof CheckoutFields;
  label: string;
  type?: string;
  autoComplete?: string;
  form: ReturnType<typeof useCheckoutForm>;
};
function Field({
  id,
  label,
  type = 'text',
  autoComplete,
  form
}: FieldProps) {
  const error = form.errors[id];
  return <div data-id="element-454" data-mp="1rslg9bba4q3i">
      <label htmlFor={`co-${id}`} className="text-sm font-medium" data-id="element-455" data-mp="1rr0i7bprap0w">{label}</label>
      <input id={`co-${id}`} type={type} autoComplete={autoComplete} value={form.fields[id]} onChange={e => form.update(id, e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `co-${id}-err` : undefined} className={`mt-1.5 h-12 w-full rounded-xl border bg-white px-4 text-ink focus:outline-none ${error ? 'border-clay focus:border-clay' : 'border-line focus:border-ink'}`} data-id="element-456" data-mp="27alan0t7r40o" />
      {error && <p id={`co-${id}-err`} className="mt-1.5 text-sm text-clay-dark" data-id="element-457" data-mp="1jkilotklie6a">{error}</p>}
    </div>;
}
function Option({
  name,
  checked,
  onChange,
  children
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}) {
  return <label className={`flex cursor-pointer items-center gap-4 rounded-xl border bg-white px-4 py-4 transition-colors duration-150 ${checked ? 'border-ink ring-1 ring-ink' : 'border-line hover:border-muted'}`} data-id="element-458" data-mp="1ahzmcjg0qjos">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="h-4 w-4 accent-[#1C1714]" data-id="element-459" data-mp="3c9rmr2xv5p43" />
      {children}
    </label>;
}